/*
 * Copyright (c) 2025 BuyerView.ai
 * All rights reserved.
 * Unauthorized copying prohibited.
 */

export interface BlogFAQ {
  question: string;
  answer: string;
}

export interface BlogPostMeta {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  author: string;
  readingTime: string;
  image?: string;
  imageAlt?: string;
  /** Plain-text description (~155 chars) used for <meta description>, og:description, twitter:description, JSON-LD description. Falls back to excerpt if absent. */
  description?: string;
  /** Comma-separated keywords. Optional. */
  keywords?: string;
  /** ISO date of last meaningful edit. Falls back to `date`. */
  modifiedDate?: string;
  /** Article category (e.g. "Property market"). */
  section?: string;
  /** Author profile URL for rel="author" + JSON-LD Person.url. */
  authorUrl?: string;
  /** Canonical URL override. Defaults to https://buyerview.ai/blog/<slug>. */
  canonicalUrl?: string;
}

export interface BlogPost extends BlogPostMeta {
  content: string;
  /** FAQ entries parsed from `faqs:` YAML block. Each becomes a JSON-LD Question. */
  faqs: BlogFAQ[];
}

const modules = import.meta.glob('../content/blog/*.md', { query: '?raw', import: 'default', eager: true }) as Record<string, string>;

function parseFrontmatter(raw: string): { data: Record<string, string>; content: string } {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);
  if (!match) return { data: {}, content: raw };
  const data: Record<string, string> = {};
  match[1].split(/\r?\n/).forEach(line => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) return;
    const colon = line.indexOf(':');
    if (colon <= 0) return;
    const key = line.slice(0, colon).trim();
    let value = line.slice(colon + 1).trim();
    const hashIndex = value.indexOf(' #');
    if (hashIndex >= 0) value = value.slice(0, hashIndex).trim();
    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1);
    }
    data[key] = value;
  });
  return { data, content: match[2] };
}

function estimateReadingTime(content: string): string {
  const words = content.trim().split(/\s+/).length;
  const minutes = Math.max(1, Math.round(words / 200));
  return `${minutes} min read`;
}

function slugFromPath(path: string): string {
  const file = path.split('/').pop() || '';
  return file.replace(/\.md$/, '');
}

/**
 * Parses an FAQ section from the rendered markdown content so JSON-LD FAQPage
 * stays in sync with the visible Q&A — no separate data block to drift.
 * Looks for an h2 like "## Frequently asked questions" (or "## FAQs"), then
 * captures each `### Question` + following paragraph(s) until the next h2/h3.
 */
function extractFAQs(content: string): BlogFAQ[] {
  const faqs: BlogFAQ[] = [];
  const sectionRe = /^##\s+(?:Frequently asked questions|FAQs?)\s*$([\s\S]*?)(?=^##\s|$(?![\s\S]))/im;
  const sectionMatch = content.match(sectionRe);
  if (!sectionMatch) return faqs;
  const body = sectionMatch[1];
  const qRe = /^###\s+(.+?)\s*\n+([\s\S]*?)(?=\n###\s|\n##\s|$)/gm;
  let m: RegExpExecArray | null;
  while ((m = qRe.exec(body)) !== null) {
    const question = m[1].trim();
    const answer = m[2]
      .trim()
      .replace(/\r?\n+/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .replace(/\*\*(.+?)\*\*/g, '$1') // strip bold markers from answer text
      .replace(/\*(.+?)\*/g, '$1'); // strip italic markers
    if (question && answer) faqs.push({ question, answer });
  }
  return faqs;
}

const posts: BlogPost[] = Object.entries(modules)
  .map(([path, raw]) => {
    const { data, content } = parseFrontmatter(raw);
    return {
      slug: data.slug || slugFromPath(path),
      title: data.title || 'Untitled',
      date: data.date || '',
      excerpt: data.excerpt || '',
      author: data.author || 'BuyerView Team',
      readingTime: estimateReadingTime(content),
      image: data.image || undefined,
      imageAlt: data.imageAlt || data.title || '',
      description: data.description || undefined,
      keywords: data.keywords || undefined,
      modifiedDate: data.modifiedDate || undefined,
      section: data.section || undefined,
      authorUrl: data.authorUrl || undefined,
      canonicalUrl: data.canonicalUrl || undefined,
      content,
      faqs: extractFAQs(content),
    };
  })
  .sort((a, b) => (a.date < b.date ? 1 : -1));

export function getAllPosts(): BlogPostMeta[] {
  return posts.map(({ content: _content, faqs: _faqs, ...meta }) => meta);
}

export function getPostBySlug(slug: string): BlogPost | undefined {
  return posts.find(p => p.slug === slug);
}
